import { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ArrowCounterClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { useNavigate } from "react-router";
import noSearchRecords from "../../assets/svg/no-matching-records.svg";
import useApiQuery from "../../hooks/useApiQuery";
import {
  getProductsForUniversalSearch,
  seachPlaceholder,
} from "../../utilities/service";
import { useAuth } from "../../hooks/AuthContext";
import SearchCategoryList from "./SearchCategoryList";
import { FilteredList } from "./SearchBar";
import { motion } from "framer-motion";

const MobileSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [searchState, setSearchState] = useState("empty");
  const [filteredData, setFilteredData] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [index, setIndex] = useState(0);

  const inputRef = useRef(null);

  const { data: responseData } = useApiQuery({
    queryKey: ["getProductsForUniversalSearch", user?.region_id],
    queryFn: () => getProductsForUniversalSearch(user?.region_id),
  });
  const products = responseData?.data?.products || [];
  const { data, error, isLoading } = useApiQuery({
    queryKey: ["seachPlaceholder"],
    queryFn: () => seachPlaceholder(),
  });
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (data?.length > 0) {
      const interval = setInterval(() => {
        setIndex((prevIndex) => (prevIndex + 1) % data?.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [data]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (value.trim() === "") {
      setSearchState("empty");
      setFilteredData([]);
      return;
    }

    const matched = products.filter((product) =>
      product.name?.toLowerCase().includes(value?.toLowerCase())
    );
    if (matched.length > 0) {
      setSearchState("suggestions");
      setFilteredData(matched);
    } else {
      setSearchState("no-results");
      setFilteredData([]);
    }
  };

  const handleClear = () => {
    setSearchText("");
    setSearchState("empty");
    setFilteredData([]);
  };

  const handleSelect = (item) => {
    const text = item?.name || item; // from suggestion or recent
    setSearchText(text);
    if (!recentSearches.includes(text)) {
      setRecentSearches((prev) => [text, ...prev.slice(0, 4)]);
    }
    navigate(`/product-description/${item?.product_name}/${item?.id}`);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "#fff",
        zIndex: 1300,
        px: 2,
        pt: 2,
        overflowY: "auto",
      }}
    >
      {/* Header with back and search field */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <TextField
          fullWidth
          placeholder="Search "
          value={searchText}
          onChange={handleSearchChange}
          inputRef={inputRef}
          sx={{
            "& input": {
              fontSize: "14px",
              fontWeight: 600,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchText ? (
                  <IconButton onClick={handleClear} edge="end">
                    <CloseIcon />
                  </IconButton>
                ) : (
                  <MagnifyingGlass
                    size={24}
                    style={{ color: "var(--neutral-600)" }}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />
        {searchText === "" && (
          <motion.span
            key={index}
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -10, opacity: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              left: "132px",
              top: "32px",
              fontSize: "14px",
              fontWeight: 600,
              color: "var(--neutral-black)",
              pointerEvents: "none",
            }}
          >
            {data?.length > 0 ? `"${data[index]}"` : "Chocolate Cake"}
          </motion.span>
        )}
      </Box>

      {/* Suggestions / No Results / Recent */}
      {searchState === "empty" && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Recent Searches
          </Typography>
          {recentSearches.length > 0 ? (
            recentSearches.map((item, i) => (
              <Box
                key={i}
                onClick={() => handleSelect(item)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <ArrowCounterClockwise size={20} style={{ marginRight: 8 }} />
                <Typography>{item}</Typography>
              </Box>
            ))
          ) : (
            <Typography>No recent searches</Typography>
          )}
        </>
      )}

      {searchState === "suggestions" && (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              mb: 2,
              textAlign: "left",
              color: "var(--neutral-black)",
            }}
          >
            Related search
          </Typography>
          <FilteredList
            filteredData={filteredData}
            searchText={searchText}
            handleSelectSuggestion={handleSelect}
          />
        </>
      )}

      {searchState === "no-results" && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <img src={noSearchRecords} alt="No results" width="60%" />
          <Typography sx={{ mt: 2 }}>
            Sorry, no results for "{searchText}"
          </Typography>
        </Box>
      )}
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        sx={{ mt: { xs: 5, md: 2 }, mb: 1 }}
      >
        Popular search
      </Typography>
      <SearchCategoryList setIsDropdownOpen={() => {}} />
    </Box>
  );
};

export default MobileSearch;
